import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import CloseIcon from '@mui/icons-material/Close';

const CATEGORIES = [
  { slug: 'toys', label: 'Toys' },
  { slug: 'fashion', label: 'Fashion' },
  { slug: 'gaming console', label: 'Gaming & Console' },
  { slug: 'headphones', label: 'Headphones' },
  { slug: 'accessories', label: 'Accessories' },
  { slug: 'musical instrument', label: 'Musical Instrument' },
  { slug: 'Gift sets', label: 'Gift sets' },
  { slug: 'Mens Grooming', label: 'Mens Grooming' },
  { slug: 'laptops', label: 'Laptops' },
  { slug: 'phones', label: 'Phones' },
];

const BRANDS_PREVIEW = 8;

function safeDecode(value) {
  try {
    return decodeURIComponent(value);
  } catch (e) {
    return value;
  }
}

/**
 * Desktop (>= 992px): sticky sidebar.
 * Mobile: bottom sheet, opened from the "Filters" button on the search page.
 *
 * Props
 *  - getProducts / isFetching : same as before
 *  - isOpen  : whether the mobile sheet is open
 *  - onClose : called when the sheet should close (backdrop, Esc, X, link tap)
 */
function SeacrhFilter({ getProducts, isFetching, isOpen = false, onClose = () => {} }) {
  const { t } = useTranslation();
  const location = useLocation();
  const closeRef = useRef(null);
  const [showAllBrands, setShowAllBrands] = useState(false);

  const country = new URLSearchParams(location.search).get('country') || null;
  const countrySuffix = country ? `&country=${country}` : '';
  const pathParts = location.pathname.split('/');
  const isSearchRoute = pathParts.includes('search');
  const activeSlug = isSearchRoute ? safeDecode(pathParts[2] || '').toLowerCase() : '';

  const brands = [...new Set((getProducts || []).map((p) => p?.brand).filter(Boolean))];
  const visibleBrands = showAllBrands ? brands : brands.slice(0, BRANDS_PREVIEW);

  const brandLink = (brand) =>
    isSearchRoute
      ? `/search/${brand}`
      : `${location.pathname}?brand=${encodeURIComponent(brand)}${countrySuffix}`;

  // Sheet behaviour: Esc closes, page behind doesn't scroll, focus moves into the sheet.
  useEffect(() => {
    if (!isOpen) return undefined;
    const onKeyDown = (e) => {
      if (e.key === 'Escape') onClose();
    };
    const previousOverflow = document.body.style.overflow;
    document.addEventListener('keydown', onKeyDown);
    document.body.style.overflow = 'hidden';
    if (closeRef.current) closeRef.current.focus();
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = previousOverflow;
    };
  }, [isOpen, onClose]);

  return (
    <>
      {isOpen && <div className="sr-backdrop" onClick={onClose} aria-hidden="true" />}

      <aside
        id="search-filters"
        className={`sr-filter${isOpen ? ' is-open' : ''}${isFetching ? ' is-loading' : ''}`}
        aria-label={t('filters', 'Filters')}
      >
        <div className="sr-filter__head">
          <div className="sr-filter__heading">{t('filters', 'Filters')}</div>
          <button
            ref={closeRef}
            type="button"
            className="sr-icon-btn"
            onClick={onClose}
            aria-label={t('close', 'Close')}
          >
            <CloseIcon fontSize="small" />
          </button>
        </div>

        <div className="sr-filter__body">
          <section className="sr-filter__section" aria-labelledby="sr-filter-categories">
            <h2 id="sr-filter-categories" className="sr-filter__title">
              {t('other_categories')}
            </h2>
            <ul className="sr-filter__list">
              {CATEGORIES.map(({ slug, label }) => {
                const active = activeSlug === slug.toLowerCase();
                return (
                  <li key={slug}>
                    <Link
                      to={`/search/${slug}`}
                      className={`sr-filter__link${active ? ' is-active' : ''}`}
                      aria-current={active ? 'page' : undefined}
                      onClick={onClose}
                    >
                      {label}
                    </Link>
                  </li>
                );
              })}
            </ul>
          </section>

          {brands.length > 1 && (
            <section className="sr-filter__section" aria-labelledby="sr-filter-brands">
              <h2 id="sr-filter-brands" className="sr-filter__title">
                {t('brands', 'Brands')}
              </h2>
              <div className="sr-chips">
                {visibleBrands.map((brand) => (
                  <Link key={brand} to={brandLink(brand)} className="sr-chip" onClick={onClose}>
                    {brand}
                  </Link>
                ))}
              </div>

              {brands.length > BRANDS_PREVIEW && (
                <button
                  type="button"
                  className="sr-filter__more"
                  onClick={() => setShowAllBrands((v) => !v)}
                  aria-expanded={showAllBrands}
                >
                  {showAllBrands ? t('show_less') : t('show_more')}
                </button>
              )}
            </section>
          )}
        </div>

        <div className="sr-filter__foot">
          <button type="button" className="sr-btn" onClick={onClose}>
            {t('view_results', 'View results')}
          </button>
        </div>
      </aside>
    </>
  );
}

export default SeacrhFilter;